import React, { useState, useEffect } from "react";
import { authFetch } from "../../../utils/authFetch/authFetch";
import { useNavigate } from "react-router-dom";
import {
  UsersIcon,
  FolderIcon,
  CalendarIcon,
  Cog6ToothIcon,
  XMarkIcon,
  CheckIcon,
  TrashIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  PencilIcon,
  MapPinIcon,
  BellIcon,
} from "@heroicons/react/24/outline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = ({ communityId, community, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("members");
  const [loading, setLoading] = useState(false);

  // Members state
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [membersFilter, setMembersFilter] = useState("all"); // all, admin, member, pending

  // Projects state
  const [projects, setProjects] = useState([]);

  // Events state
  const [events, setEvents] = useState([]);

  // Settings state
  const [settings, setSettings] = useState({
    name: community?.name || "",
    description: community?.description || "",
    isPublic: community?.isPublic ?? true,
    tags: community?.tags || [],
    requireApproval: community?.settings?.requireApproval ?? false,
    allowMemberPosts: community?.settings?.allowMemberPosts ?? true,
    allowMemberEvents: community?.settings?.allowMemberEvents ?? true,
  });

  useEffect(() => {
    if (activeTab === "members") {
      fetchMembers();
    } else if (activeTab === "projects") {
      fetchProjects();
    } else if (activeTab === "events") {
      fetchEvents();
    }
  }, [activeTab, communityId]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      // Fetch active members
      const resActive = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/members?limit=100`,
        {},
        navigate
      );
      const dataActive = await resActive.json();
      console.log("📋 Active Members Response:", dataActive);
      if (resActive.ok) {
        setMembers(dataActive.members || []);
      } else {
        console.error("Failed to fetch active members:", dataActive);
      }

      // Fetch pending members
      const resPending = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/members/pending`,
        {},
        navigate
      );
      const dataPending = await resPending.json();
      console.log("⏳ Pending Members Response:", dataPending);
      if (resPending.ok) {
        setPendingMembers(dataPending.members || []);
      } else {
        console.error("Failed to fetch pending members:", dataPending);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/projects?limit=100`,
        {},
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/events?limit=100`,
        {},
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveMember = async (memberId) => {
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/members/${memberId}/approve`,
        { method: "POST" },
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        alert("Member approved successfully");
        fetchMembers();
      } else {
        alert(data.message || "Failed to approve member");
      }
    } catch (error) {
      console.error("Error approving member:", error);
      alert("An error occurred");
    }
  };

  const handleUpdateMemberRole = async (memberId, currentRole) => {
    const newRole = currentRole === "admin" ? "member" : "admin";
    const confirmMessage =
      newRole === "admin"
        ? "Are you sure you want to promote this member to admin?"
        : "Are you sure you want to demote this admin to member?";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/members/${memberId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role: newRole }),
        },
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        alert(`Member ${newRole === "admin" ? "promoted" : "demoted"} successfully`);
        fetchMembers();
      } else {
        alert(data.message || "Failed to update member role");
      }
    } catch (error) {
      console.error("Error updating member role:", error);
      alert("An error occurred");
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from this community?`)) {
      return;
    }

    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/members/${memberId}`,
        { method: "DELETE" },
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        alert("Member removed successfully");
        fetchMembers();
      } else {
        alert(data.message || "Failed to remove member");
      }
    } catch (error) {
      console.error("Error removing member:", error);
      alert("An error occurred");
    }
  };

  const handleToggleProjectPin = async (projectId, currentPinStatus) => {
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/projects/${projectId}/pin`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isPinned: !currentPinStatus }),
        },
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        alert(`Project ${!currentPinStatus ? "pinned" : "unpinned"} successfully`);
        fetchProjects();
      } else {
        alert(data.message || "Failed to update project");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      alert("An error occurred");
    }
  };

  const handleRemoveProject = async (projectId, projectTitle) => {
    if (!window.confirm(`Are you sure you want to remove "${projectTitle}" from this community?`)) {
      return;
    }

    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/projects/${projectId}`,
        { method: "DELETE" },
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        alert("Project removed successfully");
        fetchProjects();
      } else {
        alert(data.message || "Failed to remove project");
      }
    } catch (error) {
      console.error("Error removing project:", error);
      alert("An error occurred");
    }
  };

  const handleDeleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"?`)) {
      return;
    }

    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/events/${eventId}`,
        { method: "DELETE" },
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        alert("Event deleted successfully");
        fetchEvents();
      } else {
        alert(data.message || "Failed to delete event");
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("An error occurred");
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: settings.name,
            description: settings.description,
            isPublic: settings.isPublic,
            tags: settings.tags,
            settings: {
              requireApproval: settings.requireApproval,
              allowMemberPosts: settings.allowMemberPosts,
              allowMemberEvents: settings.allowMemberEvents,
            },
          }),
        },
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        alert("Settings updated successfully");
        if (onUpdate) onUpdate();
      } else {
        alert(data.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      alert("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = () => {
    if (membersFilter === "pending") {
      return pendingMembers;
    }
    if (membersFilter === "admin") {
      return members.filter((m) => m.role === "admin");
    }
    if (membersFilter === "member") {
      return members.filter((m) => m.role === "member");
    }
    return members;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Community Admin Panel</h2>
            <p className="text-sm text-gray-500 mt-1">{community?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4 border-b border-gray-200">
          {[
            { id: "members", label: "Members", icon: UsersIcon },
            { id: "projects", label: "Projects", icon: FolderIcon },
            { id: "events", label: "Events", icon: CalendarIcon },
            { id: "settings", label: "Settings", icon: Cog6ToothIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition ${
                activeTab === tab.id
                  ? "bg-white text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "members" && (
            <MembersTab
              members={filteredMembers()}
              loading={loading}
              filter={membersFilter}
              setFilter={setMembersFilter}
              pendingCount={pendingMembers.length}
              onApprove={handleApproveMember}
              onUpdateRole={handleUpdateMemberRole}
              onRemove={handleRemoveMember}
            />
          )}

          {activeTab === "projects" && (
            <ProjectsTab
              projects={projects}
              loading={loading}
              onTogglePin={handleToggleProjectPin}
              onRemove={handleRemoveProject}
            />
          )}

          {activeTab === "events" && (
            <EventsTab
              events={events}
              loading={loading}
              onDelete={handleDeleteEvent}
              communityId={communityId}
            />
          )}

          {activeTab === "settings" && (
            <SettingsTab
              settings={settings}
              setSettings={setSettings}
              onSubmit={handleUpdateSettings}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Members Tab Component
const MembersTab = ({ members, loading, filter, setFilter, pendingCount, onApprove, onUpdateRole, onRemove }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 text-sm rounded-lg transition ${
            filter === "all"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          All Members
        </button>
        <button
          onClick={() => setFilter("admin")}
          className={`px-4 py-2 text-sm rounded-lg transition ${
            filter === "admin"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Admins
        </button>
        <button
          onClick={() => setFilter("member")}
          className={`px-4 py-2 text-sm rounded-lg transition ${
            filter === "member"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Members
        </button>
        <button
          onClick={() => setFilter("pending")}
          className={`px-4 py-2 text-sm rounded-lg transition relative ${
            filter === "pending"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Pending Requests
          {pendingCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Members List */}
      <div className="space-y-3">
        {members.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No {filter === "pending" ? "pending requests" : "members"} found
          </div>
        ) : (
          members.map((member) => (
            <div
              key={member.id}
              className="border border-gray-200 rounded-lg p-4 flex items-center gap-4 hover:bg-gray-50 transition"
            >
              {/* Avatar */}
              <img
                src={member.profile?.photoURL || "https://via.placeholder.com/48"}
                alt={member.profile?.displayName}
                className="w-12 h-12 rounded-full object-cover"
              />

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900">
                    {member.profile?.displayName || "Unknown User"}
                  </h4>
                  {member.role === "admin" && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                      Admin
                    </span>
                  )}
                  {member.status === "pending" && (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">
                      Pending
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {member.profile?.headline || "No headline"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Joined {member.joinedAt ? new Date(member.joinedAt.seconds * 1000).toLocaleDateString() : "N/A"}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {member.status === "pending" ? (
                  <>
                    <button
                      onClick={() => onApprove(member.id)}
                      className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                      title="Approve"
                    >
                      <CheckIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onRemove(member.id, member.profile?.displayName)}
                      className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                      title="Reject"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => onUpdateRole(member.id, member.role)}
                      className={`px-3 py-1.5 text-xs rounded-lg transition ${
                        member.role === "admin"
                          ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                          : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      }`}
                      title={member.role === "admin" ? "Demote to Member" : "Promote to Admin"}
                    >
                      <ShieldCheckIcon className="w-4 h-4 inline mr-1" />
                      {member.role === "admin" ? "Demote" : "Promote"}
                    </button>
                    <button
                      onClick={() => onRemove(member.id, member.profile?.displayName)}
                      className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                      title="Remove Member"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Projects Tab Component
const ProjectsTab = ({ projects, loading, onTogglePin, onRemove }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Community Projects ({projects.length})
        </h3>
      </div>

      <div className="space-y-3">
        {projects.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No projects in this community yet
          </div>
        ) : (
          projects.map((item) => (
            <div
              key={item.linkId}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">
                      {item.project?.title || "Untitled Project"}
                    </h4>
                    {item.isPinned && (
                      <MapPinIcon className="w-4 h-4 text-blue-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {item.project?.description || "No description"}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Added {item.addedAt ? new Date(item.addedAt.seconds * 1000).toLocaleDateString() : "N/A"}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onTogglePin(item.project.id, item.isPinned)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition ${
                      item.isPinned
                        ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <MapPinIcon className="w-4 h-4 inline mr-1" />
                    {item.isPinned ? "Unpin" : "Pin"}
                  </button>
                  <button
                    onClick={() => onRemove(item.project.id, item.project?.title)}
                    className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                    title="Remove Project"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Events Tab Component
const EventsTab = ({ events, loading, onDelete, communityId }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Community Events ({events.length})
        </h3>
      </div>

      <div className="space-y-3">
        {events.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No events scheduled yet
          </div>
        ) : (
          events.map((event) => (
            <div
              key={event.id}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">{event.title}</h4>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        event.status === "upcoming"
                          ? "bg-green-100 text-green-700"
                          : event.status === "ongoing"
                          ? "bg-blue-100 text-blue-700"
                          : event.status === "completed"
                          ? "bg-gray-100 text-gray-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {event.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>
                      📅 {event.startDate ? new Date(event.startDate.seconds * 1000).toLocaleDateString() : "N/A"}
                    </span>
                    <span>👥 {event.attendeeCount || 0} attending</span>
                    {event.location?.type && <span>📍 {event.location.type}</span>}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/communities/${communityId}/events/${event.id}/edit`)}
                    className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                    title="Edit Event"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(event.id, event.title)}
                    className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                    title="Delete Event"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ settings, setSettings, onSubmit, loading }) => {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Community Settings</h3>

        {/* Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Community Name
          </label>
          <input
            type="text"
            value={settings.name}
            onChange={(e) => setSettings({ ...settings, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={settings.description}
            onChange={(e) => setSettings({ ...settings, description: e.target.value })}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Tags */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            value={settings.tags.join(", ")}
            onChange={(e) =>
              setSettings({
                ...settings,
                tags: e.target.value.split(",").map((tag) => tag.trim()).filter(Boolean),
              })
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., tech, startup, web3"
          />
        </div>

        {/* Privacy */}
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.isPublic}
              onChange={(e) => setSettings({ ...settings, isPublic: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Public Community</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Public communities can be discovered and viewed by anyone
          </p>
        </div>

        {/* Member Permissions */}
        <div className="border-t border-gray-200 pt-4 mt-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3">Member Permissions</h4>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.requireApproval}
                onChange={(e) => setSettings({ ...settings, requireApproval: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Require admin approval for new members</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.allowMemberPosts}
                onChange={(e) => setSettings({ ...settings, allowMemberPosts: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Allow members to add projects</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.allowMemberEvents}
                onChange={(e) => setSettings({ ...settings, allowMemberEvents: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Allow members to create events</span>
            </label>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

export default AdminPanel;
